import { Button, Pill } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { NavLink } from 'react-router';

import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useTimeZone } from '../../components/date_formatting/timezone_context';
import { formatMarkdownTimestamps } from '../../components/date_formatting/utils';
import { getTableHeaderClassNames } from '../../components/table';
import { Table } from '../../lib/table';
import { Routes } from '../../routes';
import { TransportAnalyzerResultNode } from '../containers/transport_analyzer_container';

import styles from './transport_analyzer_results_list.module.css';

const formatDuration = (createdAt: string, finishedAt: string | null): string => {
  if (!finishedAt) {
    return '-';
  }

  const start = new Date(createdAt).getTime();
  const end = new Date(finishedAt).getTime();
  const durationMs = end - start;

  if (durationMs < 0) {
    return '-';
  }

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

export interface Props {
  results: TransportAnalyzerResultNode[];
}

class TransportAnalyzerResultsTable extends Table<TransportAnalyzerResultNode> {}

const statusKindMap: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
  green: 'success',
  red: 'danger',
  yellow: 'warning',
  orange: 'warning',
};

const DebugLogButton: React.FC<{
  debugLog: Array<{ title: string | null; duration: number | null; logMessage: string | null } | null> | null;
  analyzeId: string;
  onClick: () => void;
}> = ({ debugLog, onClick }) => {
  return (
    <Button size="small" disabled={!debugLog || debugLog.length === 0} onClick={() => onClick()}>
      Debug log
    </Button>
  );
};

export const TransportAnalyzerResultsList: React.FC<Props> = ({ results }) => {
  const { timeZone } = useTimeZone();
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const [selectedDebugLogId, setSelectedDebugLogId] = useState<string | null>(null);

  const toggleRow = (analyzeId: string) => {
    setExpandedRowIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(analyzeId)) {
        newSet.delete(analyzeId);
        // Clear debug log selection when closing the row
        if (selectedDebugLogId === analyzeId) {
          setSelectedDebugLogId(null);
        }
      } else {
        newSet.add(analyzeId);
      }
      return newSet;
    });
  };

  const handleDebugLogClick = (analyzeId: string) => {
    setSelectedDebugLogId(selectedDebugLogId === analyzeId ? null : analyzeId);
  };

  return (
    <TransportAnalyzerResultsTable
      className="ui-table compact-table"
      data={results}
      tableHeaders={{
        columns: [
          {
            keyPath: 'analyzeId',
            value: 'Analyze ID',
            className: (sortBy) => `right aligned ${getTableHeaderClassNames(sortBy)}`,
          },
          { keyPath: 'tourId', value: 'Tour ID' },
          { value: 'Actions' },
          { keyPath: 'companyName', value: 'Company' },
          { keyPath: 'companyType', value: 'Company Type' },
          { keyPath: 'status', value: 'Status' },
          { keyPath: 'createdByUserName', value: 'Created By' },
          { keyPath: 'createdAt', value: 'Created At' },
          { value: 'Duration' },
          { value: 'Error' },
        ],
        defaultClassName: getTableHeaderClassNames,
      }}
      defaultSortBy={{ keyPath: 'createdAt', value: 'DESC' }}>
      {({ row }) => {
        const { data } = row;
        const statusColor =
          data.status === 'COMPLETED'
            ? 'green'
            : data.status === 'FAILED'
              ? 'red'
              : data.status === 'IN_PROGRESS'
                ? 'yellow'
                : 'grey';
        const isExpanded = expandedRowIds.has(data.analyzeId);
        const hasDebugLog = data.debugLog && data.debugLog.length > 0;
        const hasContentToShow = data.result || hasDebugLog;

        return (
          <>
            <tr key={row.index}>
              <td style={{ textAlign: 'right' }}>{data.analyzeId}</td>
              <td>
                <NavLink to={Routes.TourAnalyze.generatePath({ tour_id: data.tourId })}>{data.tourId}</NavLink>
              </td>
              <td>
                <Button
                  size="small"
                  disabled={!hasContentToShow}
                  onClick={() => toggleRow(data.analyzeId)}
                  kind={isExpanded ? 'primary' : 'neutral'}>
                  Results
                </Button>
              </td>
              <td>
                {data.companyId && data.companyName ? (
                  <NavLink to={Routes.Company.generatePath({ company_id: data.companyId })}>{data.companyName}</NavLink>
                ) : (
                  (data.companyName ?? '-')
                )}
              </td>
              <td>{data.companyType ?? '-'}</td>
              <td>
                <Pill kind={statusKindMap[statusColor] ?? 'neutral'}>{data.status}</Pill>
              </td>
              <td>
                {data.createdByUserId && data.createdByUserName ? (
                  <NavLink to={Routes.User.generatePath({ user_id: data.createdByUserId })}>
                    {data.createdByUserName}
                  </NavLink>
                ) : (
                  (data.createdByUserName ?? '-')
                )}
              </td>
              <td>
                <FormattedDate date={data.createdAt} />
              </td>
              <td>{formatDuration(data.createdAt, data.finishedAt)}</td>
              <td>{data.error ? <>{data.error}</> : null}</td>
            </tr>
            {isExpanded && hasContentToShow && (
              <tr>
                <td colSpan={5} className={styles.expandedRowCell}>
                  {data.result ? (
                    <div className={styles.markdownContent}>
                      <ReactMarkdown>{formatMarkdownTimestamps(data.result, { timeZone })}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className={styles.markdownContent}>
                      <em>No result available</em>
                    </div>
                  )}
                </td>
                <td colSpan={5} className={styles.expandedRowCell}>
                  {hasDebugLog && (
                    <>
                      <div className={styles.debugLogButtonContainer}>
                        <DebugLogButton
                          debugLog={data.debugLog}
                          analyzeId={data.analyzeId}
                          onClick={() => handleDebugLogClick(data.analyzeId)}
                        />
                      </div>
                      {selectedDebugLogId === data.analyzeId && data.debugLog && (
                        <div className={styles.debugLogContainer}>
                          {data.debugLog.map((log, index) => (
                            <div key={index} className={styles.debugLogItem}>
                              {log?.title && <div className={styles.debugLogTitle}>{log.title}</div>}
                              {notNil(log?.duration) && (
                                <div className={styles.debugLogDuration}>Duration: {log.duration.toFixed(2)}s</div>
                              )}
                              {log?.logMessage && <pre className={styles.debugLogMessage}>{log.logMessage}</pre>}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {!hasDebugLog && (
                    <div className={styles.markdownContent}>
                      <em>No debug log available</em>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </>
        );
      }}
    </TransportAnalyzerResultsTable>
  );
};
